/**
 * triggers.js - Event binding for various trigger types
 *
 * Supports:
 * - click (default for links/buttons)
 * - submit (default for forms)
 * - change (for selects/checkboxes)
 * - input (debounced, for text inputs)
 * - visible (IntersectionObserver)
 * - load (immediate on init)
 */

import { resolve, DEFAULTS, hasAttr } from './cascade.js';
import { handleRequest, getURL, shouldSkip } from './request.js';

// Track initialized elements to avoid double-binding
const INIT_KEY = '_htmlstar';

/**
 * Create a debounced version of a function
 *
 * @param {Function} fn - Function to debounce
 * @param {number} ms - Delay in milliseconds
 * @returns {Function}
 */
export function debounce(fn, ms) {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn(...args), ms);
  };
}

/**
 * Create a throttled version of a function
 *
 * @param {Function} fn - Function to throttle
 * @param {number} ms - Delay in milliseconds
 * @returns {Function}
 */
export function throttle(fn, ms) {
  let lastRun = 0;
  let timeout;
  return (...args) => {
    const now = Date.now();
    if (now - lastRun >= ms) {
      fn(...args);
      lastRun = now;
    } else {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        fn(...args);
        lastRun = Date.now();
      }, ms - (now - lastRun));
    }
  };
}

/**
 * Initialize click handlers for implicit links and buttons
 *
 * Supports data-instant attribute for mousedown navigation (feels ~100ms faster)
 *
 * @param {HTMLElement|Document} root - Root element to search within
 * @param {Function} onInit - Callback for reinitializing new content
 */
export function initClickTriggers(root, onInit) {
  // Select links/buttons that DO NOT have an explicit trigger
  const elements = root.querySelectorAll('a[href]:not([data-trigger]), button[formaction]:not([data-trigger]), [data-href]:not([data-trigger])');

  for (const el of elements) {
    if (el[INIT_KEY]) continue;
    el[INIT_KEY] = true;

    // Only bind if element has a target (directly or inherited)
    const target = resolve(el, 'target');
    if (!target) continue;

    // Check for instant mode (mousedown instead of click)
    if (hasAttr(el, 'instant')) {
      let handled = false;

      el.addEventListener('mousedown', e => {
        if (e.button !== 0) return; // Left click only
        const url = getURL(el);
        if (shouldSkip(el, url)) return;
        handled = true;
        handleRequest(el, e, onInit);
      });

      // Prevent click from firing after mousedown navigation
      el.addEventListener('click', e => {
        if (handled) {
          handled = false;
          e.preventDefault();
          return;
        }
        handleRequest(el, e, onInit);
      });
    } else {
      el.addEventListener('click', e => handleRequest(el, e, onInit));
    }
  }
}

/**
 * Initialize submit handlers for implicit forms
 *
 * @param {HTMLElement|Document} root - Root element to search within
 * @param {Function} onInit - Callback for reinitializing new content
 */
export function initSubmitTriggers(root, onInit) {
  const forms = root.querySelectorAll('form[action]:not([data-trigger])');

  for (const form of forms) {
    if (form[INIT_KEY]) continue;
    form[INIT_KEY] = true;

    // Only bind if form has a target
    const target = resolve(form, 'target');
    if (!target) continue;

    form.addEventListener('submit', e => handleRequest(form, e, onInit));
  }
}

/**
 * Initialize explicit triggers defined by data-trigger
 * Supports modifiers: debounce:<ms>, throttle:<ms>, delay:<ms>, once
 *
 * @param {HTMLElement|Document} root - Root element to search within
 * @param {Function} onInit - Callback for reinitializing new content
 */
export function initExplicitTriggers(root, onInit) {
  const elements = root.querySelectorAll('[data-trigger]');

  for (const el of elements) {
    if (el[INIT_KEY]) continue;
    el[INIT_KEY] = true;

    const [type, ...modifiers] = el.dataset.trigger.split(/\s+/);
    
    // Base handler
    let handler = (e) => handleRequest(el, e, onInit);
    
    // Apply modifiers
    let delayMs = 0;
    
    for (const mod of modifiers) {
      if (mod.startsWith('debounce:')) {
        const ms = parseInt(mod.split(':')[1], 10) || DEFAULTS.debounce;
        handler = debounce(handler, ms);
      } else if (mod.startsWith('throttle:')) {
        const ms = parseInt(mod.split(':')[1], 10) || 300;
        handler = throttle(handler, ms);
      } else if (mod.startsWith('delay:')) {
        delayMs = parseInt(mod.split(':')[1], 10) || 0;
      } else if (mod === 'once') {
        const original = handler;
        handler = (...args) => {
          original(...args);
          // Remove listener logic depends on event type, handled below if needed
          // For simple generic handler, 'once' option in addEventListener is better
          // but we are wrapping.
        };
      }
    }

    // Apply delay wrapper if needed
    if (delayMs > 0) {
      const original = handler;
      handler = (...args) => setTimeout(() => original(...args), delayMs);
    }

    // Special types
    if (type === 'visible') {
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            handler(null); // No event object
            observer.unobserve(entry.target);
          }
        });
      }, { rootMargin: '100px' });
      observer.observe(el);
      continue;
    }

    if (type === 'load') {
      // Execute immediately (next tick to allow init to finish)
      setTimeout(() => handler(null), 0);
      continue;
    }

    // Standard events
    const opts = { once: modifiers.includes('once') };
    el.addEventListener(type, handler, opts);
  }
}

/**
 * Initialize all trigger types
 *
 * @param {HTMLElement|Document} root - Root element to search within
 * @param {Function} onInit - Callback for reinitializing new content
 */
export function initTriggers(root, onInit) {
  // Explicit triggers take precedence
  initExplicitTriggers(root, onInit);
  
  // Implicit triggers
  initClickTriggers(root, onInit);
  initSubmitTriggers(root, onInit);
}
